import { useEffect, useMemo, useState } from 'react';
import {
  CatalystRow,
  DOCUMENT_STATUSES,
  DOCUMENT_TYPES,
  DocumentStatus,
  DocumentType,
  getProjectDocumentDownloadUrl,
  PROJECT_HEALTHS,
  ProjectDocument,
  ProjectDocumentPayload,
  ProjectHealth,
  ProjectHistoryEntry,
  ProjectIssuePayload,
  QualityIssue,
  QUALITY_SEVERITIES,
  QUALITY_STATUSES,
  QualitySeverity,
  QualityStatus,
  TransitionProjectPayload,
} from '../../services/api.service';

type ProjectViewTab = 'summary' | 'workflow' | 'quality' | 'documents' | 'activity';

interface ProjectViewDialogProps {
  busy: boolean;
  canManage: boolean;
  open: boolean;
  project: CatalystRow | null;
  transitioning: boolean;
  onArchive: (project: CatalystRow) => Promise<void>;
  onClose: () => void;
  onCreateDocument: (
    project: CatalystRow,
    payload: ProjectDocumentPayload
  ) => Promise<void>;
  onCreateIssue: (
    project: CatalystRow,
    payload: ProjectIssuePayload
  ) => Promise<void>;
  onDeleteDocument: (
    project: CatalystRow,
    document: ProjectDocument
  ) => Promise<void>;
  onDeleteIssue: (
    project: CatalystRow,
    issue: QualityIssue
  ) => Promise<void>;
  onEdit: (project: CatalystRow) => void;
  onTransition: (
    project: CatalystRow,
    payload: TransitionProjectPayload
  ) => Promise<void>;
  onUpdateDocument: (
    project: CatalystRow,
    document: ProjectDocument,
    payload: Partial<ProjectDocumentPayload>
  ) => Promise<void>;
  onUpdateIssue: (
    project: CatalystRow,
    issue: QualityIssue,
    payload: Partial<ProjectIssuePayload>
  ) => Promise<void>;
}

interface IssueDraft {
  description: string;
  owner: string;
  severity: QualitySeverity;
  status: QualityStatus;
  title: string;
}

interface DocumentDraft {
  file: File | null;
  lastUpdatedNote: string;
  owner: string;
  reference: string;
  status: DocumentStatus;
  title: string;
  type: DocumentType;
}

const EVENT_LABELS: Record<ProjectHistoryEntry['type'], string> = {
  archived: 'Archived',
  created: 'Created',
  document: 'Document activity',
  health: 'Health change',
  issue: 'Issue activity',
  stage: 'Stage change',
};

const VIEW_TABS: Array<{ id: ProjectViewTab; label: string }> = [
  { id: 'summary', label: 'Summary' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'quality', label: 'Quality' },
  { id: 'documents', label: 'Documents' },
  { id: 'activity', label: 'Activity' },
];

function formatDate(value?: string | null): string {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) {
    return 'No file';
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function createEmptyIssueDraft(): IssueDraft {
  return {
    description: '',
    owner: '',
    severity: 'P3',
    status: 'Open',
    title: '',
  };
}

function createIssueDraft(issue: QualityIssue): IssueDraft {
  return {
    description: issue.description ?? '',
    owner: issue.owner ?? '',
    severity: issue.severity,
    status: issue.status,
    title: issue.title,
  };
}

function createEmptyDocumentDraft(): DocumentDraft {
  return {
    file: null,
    lastUpdatedNote: '',
    owner: '',
    reference: '',
    status: 'Requested',
    title: '',
    type: 'Drawing',
  };
}

function createDocumentDraft(document: ProjectDocument): DocumentDraft {
  return {
    file: null,
    lastUpdatedNote: document.lastUpdatedNote ?? '',
    owner: document.owner ?? '',
    reference: document.reference ?? '',
    status: document.status,
    title: document.title,
    type: document.type,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export default function ProjectViewDialog({
  busy,
  canManage,
  open,
  project,
  transitioning,
  onArchive,
  onClose,
  onCreateDocument,
  onCreateIssue,
  onDeleteDocument,
  onDeleteIssue,
  onEdit,
  onTransition,
  onUpdateDocument,
  onUpdateIssue,
}: ProjectViewDialogProps) {
  const [activeTab, setActiveTab] = useState<ProjectViewTab>('summary');
  const [note, setNote] = useState('');
  const [issueDraft, setIssueDraft] = useState<IssueDraft>(createEmptyIssueDraft());
  const [documentDraft, setDocumentDraft] = useState<DocumentDraft>(
    createEmptyDocumentDraft()
  );
  const [issueEditorId, setIssueEditorId] = useState<null | string>(null);
  const [documentEditorId, setDocumentEditorId] = useState<null | string>(null);
  const [issueBusy, setIssueBusy] = useState(false);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [issueError, setIssueError] = useState('');
  const [documentError, setDocumentError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveTab('summary');
    setNote('');
    setIssueEditorId(null);
    setDocumentEditorId(null);
    setIssueDraft(createEmptyIssueDraft());
    setDocumentDraft(createEmptyDocumentDraft());
    setIssueError('');
    setDocumentError('');
  }, [open, project?._id]);

  const timeline = useMemo(() => {
    if (!project?.history?.length) {
      return [];
    }

    return [...project.history].sort((left, right) => {
      const leftTime = left.at ? new Date(left.at).getTime() : 0;
      const rightTime = right.at ? new Date(right.at).getTime() : 0;
      return rightTime - leftTime;
    });
  }, [project?.history]);

  const qualityIssues = useMemo(() => {
    return [...(project?.qualityIssues ?? [])].sort((left, right) => {
      const leftRank = QUALITY_SEVERITIES.indexOf(left.severity);
      const rightRank = QUALITY_SEVERITIES.indexOf(right.severity);

      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      return left.title.localeCompare(right.title);
    });
  }, [project?.qualityIssues]);

  const documents = useMemo(() => {
    return [...(project?.documents ?? [])].sort((left, right) => {
      const leftTime = left.updatedAt ? new Date(left.updatedAt).getTime() : 0;
      const rightTime = right.updatedAt ? new Date(right.updatedAt).getTime() : 0;
      return rightTime - leftTime;
    });
  }, [project?.documents]);

  if (!open || !project) {
    return null;
  }

  const activeProject = project;
  const workflow = activeProject.workflow;
  const coverage = activeProject.workflowSummary?.documentCoverage;
  const nextStage = workflow?.nextStage ?? null;
  const isFinalStage = nextStage === null;
  const workflowStatusLabel = isFinalStage
    ? 'Complete'
    : workflow?.canAdvance
      ? 'Ready'
      : 'Blocked';
  const workflowStatusClassName = isFinalStage
    ? 'workflow-status workflow-status--complete'
    : workflow?.canAdvance
      ? 'workflow-status workflow-status--ready'
      : 'workflow-status workflow-status--blocked';
  const workflowChecklist = isFinalStage
    ? [{ done: true, label: 'Project is already in the final workflow stage.' }]
    : (workflow?.checklist ?? []);
  const openIssueCount = qualityIssues.filter((issue) => issue.status !== 'Closed').length;
  const pendingDocumentCount = documents.filter(
    (document) => document.status === 'Missing' || document.status === 'Requested'
  ).length;

  async function runTransition(payload: TransitionProjectPayload) {
    if (transitioning) {
      return;
    }

    if (payload.health && payload.health === activeProject.health) {
      return;
    }

    await onTransition(activeProject, {
      ...payload,
      note: note.trim() || undefined,
    });
    setNote('');
  }

  function openIssueEditor(issue?: QualityIssue) {
    setActiveTab('quality');
    setIssueError('');
    setIssueEditorId(issue?._id ?? 'create');
    setIssueDraft(issue ? createIssueDraft(issue) : createEmptyIssueDraft());
  }

  function openDocumentEditor(document?: ProjectDocument) {
    setActiveTab('documents');
    setDocumentError('');
    setDocumentEditorId(document?._id ?? 'create');
    setDocumentDraft(
      document ? createDocumentDraft(document) : createEmptyDocumentDraft()
    );
  }

  async function submitIssue() {
    const trimmedTitle = issueDraft.title.trim();
    if (!trimmedTitle) {
      setIssueError('Issue title is required.');
      return;
    }

    setIssueBusy(true);
    setIssueError('');

    try {
      const payload: ProjectIssuePayload = {
        description: issueDraft.description.trim(),
        owner: issueDraft.owner.trim(),
        severity: issueDraft.severity,
        status: issueDraft.status,
        title: trimmedTitle,
      };

      const activeIssue = qualityIssues.find((issue) => issue._id === issueEditorId);
      if (issueEditorId === 'create' || !activeIssue) {
        await onCreateIssue(activeProject, payload);
      } else {
        await onUpdateIssue(activeProject, activeIssue, payload);
      }

      setIssueEditorId(null);
      setIssueDraft(createEmptyIssueDraft());
    } catch (error) {
      setIssueError(getErrorMessage(error, 'Could not save the quality issue.'));
    } finally {
      setIssueBusy(false);
    }
  }

  async function submitDocument() {
    const trimmedTitle = documentDraft.title.trim();
    if (!trimmedTitle) {
      setDocumentError('Document title is required.');
      return;
    }

    setDocumentBusy(true);
    setDocumentError('');

    try {
      const payload: ProjectDocumentPayload = {
        file: documentDraft.file,
        lastUpdatedNote: documentDraft.lastUpdatedNote.trim(),
        owner: documentDraft.owner.trim(),
        reference: documentDraft.reference.trim(),
        status: documentDraft.status,
        title: trimmedTitle,
        type: documentDraft.type,
      };

      const activeDocument = documents.find(
        (document) => document._id === documentEditorId
      );

      if (documentEditorId === 'create' || !activeDocument) {
        await onCreateDocument(activeProject, payload);
      } else {
        await onUpdateDocument(activeProject, activeDocument, payload);
      }

      setDocumentEditorId(null);
      setDocumentDraft(createEmptyDocumentDraft());
    } catch (error) {
      setDocumentError(getErrorMessage(error, 'Could not save the document.'));
    } finally {
      setDocumentBusy(false);
    }
  }

  function renderSummaryTab() {
    return (
      <div className="project-tab-panel project-tab-panel--stacked">
        <section className="project-overview-grid">
          <article className="project-overview-card">
            <span className="section-label">Current stage</span>
            <strong>{activeProject.stage}</strong>
            <p>
              {nextStage
                ? workflow?.canAdvance
                  ? `Ready to move into ${nextStage}.`
                  : `Next planned stage is ${nextStage}.`
                : 'This project is already in the final workflow stage.'}
            </p>
          </article>

          <article className="project-overview-card">
            <span className="section-label">Health</span>
            <strong>{activeProject.health}</strong>
            <p>Current health signal across workflow, evidence, and issue state.</p>
          </article>

          <article className="project-overview-card">
            <span className="section-label">Open issues</span>
            <strong>{openIssueCount}</strong>
            <p>Quality items still requiring review, closure, or QC confirmation.</p>
          </article>

          <article className="project-overview-card">
            <span className="section-label">Evidence status</span>
            <strong>{coverage?.approved ?? 0}</strong>
            <p>{pendingDocumentCount} pending evidence items remain in this register.</p>
          </article>
        </section>

        <section className="project-view__grid project-view__grid--split">
          <article className="project-view__card">
            <p className="section-label">Project details</p>
            <dl className="project-view__facts">
              <div>
                <dt>Client</dt>
                <dd>{activeProject.client}</dd>
              </div>
              <div>
                <dt>Owner</dt>
                <dd>{activeProject.owner}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatDate(activeProject.createdAt)}</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{formatDate(activeProject.updatedAt)}</dd>
              </div>
            </dl>
          </article>

          <article className="project-view__card">
            <p className="section-label">Execution brief</p>
            <div className="project-copy-stack">
              <div>
                <strong>Summary</strong>
                <p>{activeProject.summary?.trim() || 'No summary recorded yet.'}</p>
              </div>
              <div>
                <strong>Next action</strong>
                <p>
                  {activeProject.nextAction?.trim() || 'No next action recorded yet.'}
                </p>
              </div>
            </div>
          </article>
        </section>
      </div>
    );
  }

  function renderWorkflowTab() {
    return (
      <div className="project-tab-panel project-view__grid project-view__grid--split">
        <article className="project-view__card">
          <div className="subpanel-header">
            <div>
              <p className="section-label">Workflow gate</p>
              <h3>{nextStage ? `${activeProject.stage} to ${nextStage}` : 'Final stage'}</h3>
            </div>
            <span className={workflowStatusClassName}>{workflowStatusLabel}</span>
          </div>

          <ul className="checklist">
            {workflowChecklist.map((item) => (
              <li
                key={item.label}
                className={
                  item.done ? 'checklist__item checklist__item--done' : 'checklist__item'
                }
              >
                <span aria-hidden="true">{item.done ? '✓' : '•'}</span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>

          {isFinalStage ? (
            <p className="project-workflow__hint">
              Warranty is the terminal stage in the current workflow. Use health,
              issues, and evidence updates to manage closeout from here.
            </p>
          ) : null}

          {workflow?.reasons?.length ? (
            <div className="workflow-reasons">
              {workflow.reasons.map((reason) => (
                <p key={reason}>{reason}</p>
              ))}
            </div>
          ) : null}
        </article>

        <article className="project-view__card project-workflow">
          <div className="subpanel-header">
            <div>
              <p className="section-label">Manager actions</p>
              <h3>Transition controls</h3>
            </div>
          </div>

          {canManage ? (
            <>
              <label className="field project-workflow__note">
                <span className="field__label">Transition note</span>
                <input
                  disabled={transitioning}
                  placeholder="Explain the workflow update"
                  type="text"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>

              <div className="project-workflow__row">
                {isFinalStage ? (
                  <p className="project-workflow__hint">
                    No further stage transition is available because this project is
                    already at the final stage.
                  </p>
                ) : (
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => void runTransition({ stage: nextStage })}
                  >
                    Advance to {nextStage}
                  </button>
                )}
              </div>

              <div className="project-workflow__health">
                <span className="field__label">Set health</span>
                <div className="project-workflow__health-buttons">
                  {PROJECT_HEALTHS.map((health: ProjectHealth) => (
                    <button
                      key={health}
                      className={
                        health === activeProject.health
                          ? 'chip-button chip-button--active'
                          : 'chip-button'
                      }
                      type="button"
                      onClick={() => void runTransition({ health })}
                    >
                      {health}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="project-dialog__readonly">
              Workflow changes are restricted to manager access.
            </p>
          )}
        </article>
      </div>
    );
  }

  function renderQualityTab() {
    return (
      <div className="project-tab-panel project-detail-layout">
        <section className="project-detail-layout__main project-view__card">
          <div className="subpanel-header">
            <div>
              <p className="section-label">Quality register</p>
              <h3>Open and closed issues</h3>
            </div>
            {canManage ? (
              <button className="ghost-button" type="button" onClick={() => openIssueEditor()}>
                Add issue
              </button>
            ) : null}
          </div>

          {!qualityIssues.length ? (
            <p className="empty-copy">No quality issues recorded for this project yet.</p>
          ) : (
            <div className="subtable-shell">
              <table className="subtable">
                <thead>
                  <tr>
                    <th>Issue</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Owner</th>
                    <th>Verified</th>
                    {canManage ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {qualityIssues.map((issue) => (
                    <tr key={issue._id}>
                      <td>
                        <div className="subtable-primary">
                          <strong>{issue.title}</strong>
                          <span>{issue.description?.trim() || 'No description'}</span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`severity-badge severity-badge--${issue.severity.toLowerCase()}`}
                        >
                          {issue.severity}
                        </span>
                      </td>
                      <td>{issue.status}</td>
                      <td>{issue.owner || 'Unassigned'}</td>
                      <td>
                        {issue.verifiedAt
                          ? `${issue.verifiedBy || 'Verifier'} · ${formatDate(
                              issue.verifiedAt
                            )}`
                          : 'Pending'}
                      </td>
                      {canManage ? (
                        <td>
                          <div className="row-actions">
                            <button
                              className="link-button"
                              type="button"
                              onClick={() => openIssueEditor(issue)}
                            >
                              Edit
                            </button>
                            {issue.status !== 'Closed' ? (
                              <button
                                className="link-button"
                                type="button"
                                onClick={() =>
                                  void onUpdateIssue(activeProject, issue, {
                                    status: 'Closed',
                                  })
                                }
                              >
                                Close
                              </button>
                            ) : null}
                            <button
                              className="link-button link-button--danger"
                              type="button"
                              onClick={() => void onDeleteIssue(activeProject, issue)}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="project-detail-layout__side project-view__card">
          <div className="subpanel-header">
            <div>
              <p className="section-label">Issue editor</p>
              <h3>{issueEditorId ? 'Add or edit issue' : 'Select an action'}</h3>
            </div>
          </div>

          {issueEditorId ? (
            <div className="editor-stack">
              <label className="field">
                <span className="field__label">Issue title</span>
                <input
                  type="text"
                  value={issueDraft.title}
                  onChange={(event) =>
                    setIssueDraft((currentDraft) => ({
                      ...currentDraft,
                      title: event.target.value,
                    }))
                  }
                />
              </label>

              <label className="field">
                <span className="field__label">Owner</span>
                <input
                  type="text"
                  value={issueDraft.owner}
                  onChange={(event) =>
                    setIssueDraft((currentDraft) => ({
                      ...currentDraft,
                      owner: event.target.value,
                    }))
                  }
                />
              </label>

              <div className="editor-split">
                <label className="field">
                  <span className="field__label">Severity</span>
                  <select
                    value={issueDraft.severity}
                    onChange={(event) =>
                      setIssueDraft((currentDraft) => ({
                        ...currentDraft,
                        severity: event.target.value as QualitySeverity,
                      }))
                    }
                  >
                    {QUALITY_SEVERITIES.map((severity) => (
                      <option key={severity} value={severity}>
                        {severity}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span className="field__label">Status</span>
                  <select
                    value={issueDraft.status}
                    onChange={(event) =>
                      setIssueDraft((currentDraft) => ({
                        ...currentDraft,
                        status: event.target.value as QualityStatus,
                      }))
                    }
                  >
                    {QUALITY_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="field">
                <span className="field__label">Description</span>
                <textarea
                  rows={4}
                  value={issueDraft.description}
                  onChange={(event) =>
                    setIssueDraft((currentDraft) => ({
                      ...currentDraft,
                      description: event.target.value,
                    }))
                  }
                />
              </label>

              {issueError ? <p className="form-state form-state--error">{issueError}</p> : null}

              <div className="embedded-editor__actions">
                <button
                  className="ghost-button"
                  disabled={issueBusy}
                  type="button"
                  onClick={() => {
                    setIssueEditorId(null);
                    setIssueDraft(createEmptyIssueDraft());
                  }}
                >
                  Cancel
                </button>
                <button
                  className="primary-button"
                  disabled={issueBusy}
                  type="button"
                  onClick={() => void submitIssue()}
                >
                  {issueBusy ? 'Saving...' : 'Save issue'}
                </button>
              </div>
            </div>
          ) : (
            <p className="empty-copy">
              Choose “Add issue” or edit an existing row to manage the quality register.
            </p>
          )}
        </aside>
      </div>
    );
  }

  function renderDocumentsTab() {
    const activeDocument = documents.find((document) => document._id === documentEditorId);

    return (
      <div className="project-tab-panel project-detail-layout">
        <section className="project-detail-layout__main project-view__card">
          <div className="subpanel-header">
            <div>
              <p className="section-label">Evidence register</p>
              <h3>Uploaded and placeholder documents</h3>
            </div>
            {canManage ? (
              <button
                className="ghost-button"
                type="button"
                onClick={() => openDocumentEditor()}
              >
                Add document
              </button>
            ) : null}
          </div>

          {!documents.length ? (
            <p className="empty-copy">No documents recorded for this project yet.</p>
          ) : (
            <div className="subtable-shell">
              <table className="subtable">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>File</th>
                    <th>Owner</th>
                    {canManage ? <th>Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((document) => (
                    <tr key={document._id}>
                      <td>
                        <div className="subtable-primary">
                          <strong>{document.title}</strong>
                          <span>
                            {document.lastUpdatedNote?.trim() || 'No update note'}
                          </span>
                        </div>
                      </td>
                      <td>{document.type}</td>
                      <td>{document.status}</td>
                      <td>
                        {document.hasFile ? (
                          <button
                            className="link-button"
                            type="button"
                            onClick={() =>
                              window.open(
                                getProjectDocumentDownloadUrl(activeProject._id, document),
                                '_blank'
                              )
                            }
                          >
                            {document.fileName || 'Download'}
                          </button>
                        ) : (
                          <span className="text-muted">Placeholder only</span>
                        )}
                      </td>
                      <td>{document.owner || 'Unassigned'}</td>
                      {canManage ? (
                        <td>
                          <div className="row-actions">
                            <button
                              className="link-button"
                              type="button"
                              onClick={() => openDocumentEditor(document)}
                            >
                              Edit
                            </button>
                            {document.hasFile ? (
                              <button
                                className="link-button"
                                type="button"
                                onClick={() =>
                                  window.open(
                                    getProjectDocumentDownloadUrl(activeProject._id, document),
                                    '_blank'
                                  )
                                }
                              >
                                Download
                              </button>
                            ) : null}
                            <button
                              className="link-button link-button--danger"
                              type="button"
                              onClick={() => void onDeleteDocument(activeProject, document)}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="project-detail-layout__side project-view__card">
          <div className="subpanel-header">
            <div>
              <p className="section-label">Upload panel</p>
              <h3>{documentEditorId ? 'Add or edit document' : 'Select an action'}</h3>
            </div>
          </div>

          {documentEditorId ? (
            <div className="editor-stack">
              <label className="field">
                <span className="field__label">Document title</span>
                <input
                  type="text"
                  value={documentDraft.title}
                  onChange={(event) =>
                    setDocumentDraft((currentDraft) => ({
                      ...currentDraft,
                      title: event.target.value,
                    }))
                  }
                />
              </label>

              <div className="editor-split">
                <label className="field">
                  <span className="field__label">Type</span>
                  <select
                    value={documentDraft.type}
                    onChange={(event) =>
                      setDocumentDraft((currentDraft) => ({
                        ...currentDraft,
                        type: event.target.value as DocumentType,
                      }))
                    }
                  >
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field">
                  <span className="field__label">Status</span>
                  <select
                    value={documentDraft.status}
                    onChange={(event) =>
                      setDocumentDraft((currentDraft) => ({
                        ...currentDraft,
                        status: event.target.value as DocumentStatus,
                      }))
                    }
                  >
                    {DOCUMENT_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="field">
                <span className="field__label">Owner</span>
                <input
                  type="text"
                  value={documentDraft.owner}
                  onChange={(event) =>
                    setDocumentDraft((currentDraft) => ({
                      ...currentDraft,
                      owner: event.target.value,
                    }))
                  }
                />
              </label>

              <label className="field">
                <span className="field__label">Reference</span>
                <input
                  type="text"
                  value={documentDraft.reference}
                  onChange={(event) =>
                    setDocumentDraft((currentDraft) => ({
                      ...currentDraft,
                      reference: event.target.value,
                    }))
                  }
                />
              </label>

              <label className="field">
                <span className="field__label">Upload file</span>
                <input
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt"
                  type="file"
                  onChange={(event) =>
                    setDocumentDraft((currentDraft) => ({
                      ...currentDraft,
                      file: event.target.files?.[0] ?? null,
                    }))
                  }
                />
              </label>

              <div className="upload-meta-card">
                <strong>Attached file</strong>
                <p>
                  {documentDraft.file
                    ? `${documentDraft.file.name} · ${formatFileSize(
                        documentDraft.file.size
                      )}`
                    : activeDocument?.hasFile
                      ? `${activeDocument.fileName || 'Existing file'} · ${formatFileSize(
                          activeDocument.fileSize
                        )}`
                      : 'No file attached yet'}
                </p>
              </div>

              <label className="field">
                <span className="field__label">Update note</span>
                <textarea
                  rows={4}
                  value={documentDraft.lastUpdatedNote}
                  onChange={(event) =>
                    setDocumentDraft((currentDraft) => ({
                      ...currentDraft,
                      lastUpdatedNote: event.target.value,
                    }))
                  }
                />
              </label>

              {documentError ? (
                <p className="form-state form-state--error">{documentError}</p>
              ) : null}

              <div className="embedded-editor__actions">
                <button
                  className="ghost-button"
                  disabled={documentBusy}
                  type="button"
                  onClick={() => {
                    setDocumentEditorId(null);
                    setDocumentDraft(createEmptyDocumentDraft());
                  }}
                >
                  Cancel
                </button>
                <button
                  className="primary-button"
                  disabled={documentBusy}
                  type="button"
                  onClick={() => void submitDocument()}
                >
                  {documentBusy ? 'Saving...' : 'Save document'}
                </button>
              </div>
            </div>
          ) : (
            <p className="empty-copy">
              Choose “Add document” or edit an existing row to upload or replace files.
            </p>
          )}
        </aside>
      </div>
    );
  }

  function renderActivityTab() {
    return (
      <section className="project-tab-panel project-view__card">
        <p className="section-label">Activity timeline</p>
        {timeline.length === 0 ? (
          <p>No workflow activity recorded yet.</p>
        ) : (
          <ol className="timeline">
            {timeline.map((entry, index) => (
              <li key={`${entry.type}-${entry.at ?? index}`} className="timeline__item">
                <span
                  className={`timeline__dot timeline__dot--${entry.type}`}
                  aria-hidden="true"
                />
                <div className="timeline__content">
                  <div className="timeline__headline">
                    <strong>{EVENT_LABELS[entry.type]}</strong>
                    {entry.from || entry.to ? (
                      <span className="timeline__change">
                        {entry.from ? `${entry.from} → ` : ''}
                        {entry.to}
                      </span>
                    ) : null}
                  </div>
                  {entry.note ? <p className="timeline__note">{entry.note}</p> : null}
                  <p className="timeline__meta">
                    {entry.actor ?? 'system'} · {formatDate(entry.at)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    );
  }

  function renderActiveTab() {
    switch (activeTab) {
      case 'workflow':
        return renderWorkflowTab();
      case 'quality':
        return renderQualityTab();
      case 'documents':
        return renderDocumentsTab();
      case 'activity':
        return renderActivityTab();
      case 'summary':
      default:
        return renderSummaryTab();
    }
  }

  return (
    <div className="project-dialog-backdrop" role="presentation" onClick={onClose}>
      <section
        aria-labelledby="project-view-title"
        aria-modal="true"
        className="project-dialog project-dialog--view project-dialog--wide"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="project-dialog__header project-dialog__header--spaced">
          <div>
            <p className="project-dialog__eyebrow">Project workspace</p>
            <h2 id="project-view-title">{activeProject.name}</h2>
            <div className="project-dialog__subhead">
              <span className="project-code-chip">{activeProject.projectCode}</span>
              <span>{activeProject.client}</span>
              <span>{activeProject.owner}</span>
            </div>
          </div>
          <div className="project-view__status">
            <span className="pill pill--neutral">{activeProject.stage}</span>
            <span
              className={`pill pill--health-${activeProject.health
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              {activeProject.health}
            </span>
            <button className="ghost-button" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </header>

        <nav className="project-tabs" aria-label="Project detail tabs">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? 'project-tab project-tab--active' : 'project-tab'}
              type="button"
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="project-view__body">{renderActiveTab()}</div>

        <footer className="project-dialog__footer">
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
          {canManage ? (
            <>
              <button
                className="ghost-button"
                type="button"
                onClick={() => onEdit(activeProject)}
              >
                Edit project
              </button>
              <button
                className="danger-button"
                disabled={busy}
                type="button"
                onClick={() => void onArchive(activeProject)}
              >
                {busy ? 'Archiving...' : 'Archive'}
              </button>
            </>
          ) : (
            <span className="project-dialog__readonly">Read-only access</span>
          )}
        </footer>
      </section>
    </div>
  );
}
